<script setup lang="ts">
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { type Document } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLocaleStore } from '@/store/localStore'

  interface Props {
    document: Document
    height?: string
    allowDownload?: boolean
    allowPrint?: boolean
    showBorder?: boolean
    disablePlugins?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    height: '400px',
    allowDownload: false,
    allowPrint: false,
    showBorder: true,
    disablePlugins: false
  })

  interface OnlyOfficeInstance {
    destroyEditor: () => void
  }

  const userStore = useUserStore()
  const localeStore = useLocaleStore()
  const containerId = ref(`office-preview-${Math.random().toString(36).substr(2, 9)}`)
  const isLoading = ref(true)
  const hasError = ref(false)
  let editorInstance: OnlyOfficeInstance | null = null

  function getDocumentType(filename: string): { fileType: string; documentType: string } {
    const lower = filename.toLowerCase()

    if (lower.endsWith('.docx') || lower.endsWith('.doc')) {
      return { fileType: lower.endsWith('.docx') ? 'docx' : 'doc', documentType: 'word' }
    } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      return { fileType: lower.endsWith('.xlsx') ? 'xlsx' : 'xls', documentType: 'cell' }
    } else if (lower.endsWith('.pptx') || lower.endsWith('.ppt')) {
      return { fileType: lower.endsWith('.pptx') ? 'pptx' : 'ppt', documentType: 'slide' }
    } else if (lower.endsWith('.pdf')) {
      return { fileType: 'pdf', documentType: 'pdf' }
    }

    return { fileType: 'docx', documentType: 'word' }
  }

  async function initializePreview() {
    if (!props.document?.url) {
      hasError.value = true
      isLoading.value = false
      return
    }

    // Ensure user is loaded
    if (!userStore.user) {
      await userStore.fetchUser()
    }

    // Check if OnlyOffice API is already loaded
    if (!(window as any).DocsAPI) {
      // Load OnlyOffice script if not already loaded
      const existingScript = document.querySelector('script[src*="onlyoffice"]')
      if (!existingScript) {
        const script = document.createElement('script')
        script.src = '/onlyoffice/web-apps/apps/api/documents/api.js'
        script.onload = () => createEditor()
        script.onerror = () => {
          hasError.value = true
          isLoading.value = false
        }
        document.head.appendChild(script)
      } else {
        // Wait for existing script to load
        setTimeout(createEditor, 100)
      }
    } else {
      createEditor()
    }
  }

  function createEditor() {
    if (!(window as any).DocsAPI) {
      setTimeout(createEditor, 100)
      return
    }

    cleanupEditor()

    const { fileType, documentType } = getDocumentType(props.document.filename)

    let url = props.document.url
    if (url.includes('localhost')) {
      url = url.replace('localhost', 'caddy')
    }

    const currentLocale = localeStore.getLocale

    const config: any = {
      document: {
        fileType: fileType,
        key: `${props.document.uuid}-inline-${Date.now()}`,
        title: props.document.title || props.document.filename,
        url: url,
        permissions: {
          edit: false,
          download: props.allowDownload,
          print: props.allowPrint
        }
      },
      documentType: documentType,
      editorConfig: {
        mode: 'view',
        lang: currentLocale,
        region: currentLocale === 'de' ? 'de-DE' : 'en-US',
        customization: {
          compactHeader: true,
          compactToolbar: false,
          toolbar: false,
          hideRightMenu: true,
          hideRulers: true,
          toolbarHideFileName: true,
          toolbarNoTabs: true,
          leftPanel: false,
          features: {
            download: props.allowDownload
          },
          logo: {
            visible: false
          },
          plugins: props.disablePlugins ? false : undefined,
          uiTheme: 'theme-white'
        }
      },
      height: props.height,
      width: '100%'
    }

    // Add user info if available
    if (userStore.user) {
      config.editorConfig.user = {
        id: userStore.user.uuid,
        name: `${userStore.user.first_name} ${userStore.user.last_name}`
      }
    }

    try {
      editorInstance = new (window as any).DocsAPI.DocEditor(containerId.value, config)
      isLoading.value = false
    } catch (error) {
      console.error('Failed to create OnlyOffice preview:', error)
      hasError.value = true
      isLoading.value = false
    }
  }

  function cleanupEditor() {
    if (editorInstance) {
      try {
        editorInstance.destroyEditor()
      } catch (e) {
        console.error('Error destroying editor:', e)
      }
      editorInstance = null
    }
  }

  // Watch for document changes
  watch(
    () => props.document,
    () => {
      if (props.document) {
        initializePreview()
      }
    }
  )

  onMounted(() => {
    initializePreview()
  })

  onUnmounted(() => {
    cleanupEditor()
  })
</script>

<template>
  <div
    class="office-preview-wrapper"
    :class="{ 'with-border': showBorder }"
    :style="{ height: height }"
  >
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="preview-state"
    >
      <div class="preview-spinner"></div>
      <span>Loading preview...</span>
    </div>

    <!-- Error state -->
    <div
      v-else-if="hasError"
      class="preview-state preview-error"
    >
      <span>Unable to load preview</span>
    </div>

    <!-- Preview container -->
    <div
      v-show="!isLoading && !hasError"
      :id="containerId"
      class="preview-embed"
    ></div>
  </div>
</template>

<style scoped>
  .office-preview-wrapper {
    width: 100%;
    overflow: hidden;
    background: #ffffff;
    position: relative;
  }

  .office-preview-wrapper.with-border {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  .preview-embed {
    width: 100%;
    height: 100%;
  }

  .preview-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 0.5rem;
    color: #666;
    font-size: 0.875rem;
  }

  .preview-error {
    color: #dc3545;
  }

  .preview-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #e0e0e0;
    border-top: 3px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
